import { forwardRef } from 'react';

import type { Classnames, IceFunction } from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import testId from '@snowit/utils/testId';

import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './Textarea.module.scss';

interface ITextarea {
  label?: string;
  placeholder?: string;
  hint?: string;
  name?: string;
  defaultValue?: string;
  value?: string;
  error?: boolean;
  errorMsg?: string;
  loading?: boolean;
  readOnly?: boolean;
  required?: boolean;
  disabled?: boolean;
  autoComplete?: string;
  onChange?: IceFunction;
  onClick?: IceFunction;
  onClickButton?: IceFunction;
  onBlur?: IceFunction;
  onFocus?: IceFunction;
  onKeyDown?: IceFunction;
  onKeyUp?: IceFunction;
  id?: string;
  classNames?: Classnames<'wrapper' | 'wrapInput' | 'textarea'>;
  rowsHeight?: number;
  /** Jest/Cypress test string */
  test?: string;
}

const Textarea = forwardRef((props: ITextarea, ref: any) => {
  const {
    classNames,
    defaultValue,
    disabled,
    error,
    errorMsg,
    hint,
    label,
    loading,
    name,
    autoComplete,
    onBlur,
    onChange,
    onClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    placeholder,
    readOnly,
    required,
    test,
    value,
    ...rest
  } = props;
  const a11yH = useA11y();

  const id = a11yH.makeId(props.id);
  const hintId = a11yH.makeId(props.id, 'hint');
  const errorId = a11yH.makeId(props.id, 'error');

  const handleClick = (event) => {
    if (disabled || loading) return;
    if (typeof onClick === 'function') {
      onClick({
        eventName: 'onClick',
        name,
        id,
        value,
        event,
      });
    }
  };

  const handleChange = (event) => {
    if (disabled || loading) return;
    if (typeof onChange === 'function') {
      onChange({
        eventName: 'onChange',
        name,
        id,
        value: event.target.value,
        event,
      });
    }
  };

  const handleFocus = (event) => {
    if (disabled || loading) return;
    if (typeof onFocus === 'function') {
      onFocus({
        eventName: 'onFocus',
        name,
        id,
        value,
        event,
      });
    }
  };

  const handleBlur = (event) => {
    if (disabled || loading) return;
    if (typeof onBlur === 'function') {
      onBlur({
        eventName: 'onBlur',
        name,
        id,
        value,
        event,
      });
    }
  };

  const handleKeyDown = (event) => {
    if (disabled || loading) return;
    if (typeof onKeyDown === 'function') {
      onKeyDown({
        eventName: 'onKeyDown',
        name,
        id,
        value: event.target.value,
        event,
        key: event.key,
        keyCode: event.keyCode,
      });
    }
  };

  const handleKeyUp = (event) => {
    if (disabled || loading) return;
    if (typeof onKeyUp === 'function') {
      onKeyUp({
        eventName: 'onKeyUp',
        name,
        id,
        value: event.target.value,
        event,
        key: event.key,
        keyCode: event.keyCode,
      });
    }
  };

  const renderTextarea = () => {
    let ariaDescribedby: string | undefined;
    if (errorMsg) {
      ariaDescribedby = errorId;
    } else if (hint) {
      ariaDescribedby = hintId;
    }

    return (
      <textarea
        className={cx(s.textarea, classNames?.textarea)}
        disabled={disabled}
        id={id}
        name={name}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        placeholder={placeholder}
        readOnly={readOnly}
        ref={ref}
        required={required}
        value={value}
        defaultValue={defaultValue}
        autoComplete={autoComplete}
        aria-describedby={ariaDescribedby}
        aria-invalid={error}
        aria-label={label ? undefined : label}
        rows={props.rowsHeight}
        {...testId(test)}
        {...rest}
      />
    );
  };

  const renderLabel = () => {
    if (!label) return null;
    return (
      <Typo className={s.label} text={label} tag={'label'} size={'sm'} a11y={{ htmlFor: id }} />
    );
  };

  const renderHint = () => {
    if (error && errorMsg) return null;
    if (!hint) return null;
    return (
      <Typo className={s.hint} id={hintId} text={hint} tag={'p'} size={'md'} color={'text-l'} />
    );
  };

  const renderError = () => {
    if (!error) return null;
    if (!errorMsg) return null;
    if (error && !errorMsg) return null;
    return (
      <Typo
        className={s.hint}
        id={errorId}
        text={errorMsg}
        tag={'p'}
        size={'md'}
        color={'error'}
        a11y={{
          role: 'alert',
        }}
        {...testId(test, 'input_error')}
      />
    );
  };

  return (
    <div className={cx(s.wrapper, classNames?.wrapper)}>
      <div
        className={cx(s.wrapInput, classNames?.wrapInput, {
          [s.error]: error,
          [s.disabled]: disabled,
          [s.readOnly]: readOnly || loading,
          [s.required]: required,
        })}
        {...testId(test, 'container')}
        onClick={handleClick}
      >
        {renderTextarea()}
        {renderLabel()}
      </div>

      {renderHint()}
      {renderError()}
    </div>
  );
});

export default Textarea;
